import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { Button, Input, Modal } from "@vv/ui";
import { useState } from "react";
import type { ValidationResult } from "../domain/request.types";
import { ALL_VALIDATION_RESULTS, type CompletePayload } from "../domain/request.types";

export interface CompleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: CompletePayload) => Promise<void>;
  expectedCompletionDate: string | null;
}

export function CompleteModal({
  open,
  onClose,
  onConfirm,
  expectedCompletionDate,
}: CompleteModalProps) {
  const [validationResult, setValidationResult] = useState<ValidationResult | "">("");
  const [summary, setSummary] = useState("");
  const [actualCompletionDate, setActualCompletionDate] = useState("");
  const [extensionReason, setExtensionReason] = useState("");
  const [saving, setSaving] = useState(false);

  const isLate =
    !!expectedCompletionDate &&
    !!actualCompletionDate &&
    actualCompletionDate > expectedCompletionDate;
  const isValid =
    validationResult !== "" &&
    summary.trim().length > 0 &&
    actualCompletionDate !== "" &&
    (!isLate || extensionReason.trim().length > 0);

  async function handleConfirm() {
    setSaving(true);
    try {
      await onConfirm({
        validationResult: validationResult as ValidationResult,
        summary: summary.trim(),
        actualCompletionDate,
        extensionReason: isLate ? extensionReason.trim() : undefined,
      });
      setValidationResult("");
      setSummary("");
      setActualCompletionDate("");
      setExtensionReason("");
    } catch {
      // Already toasted by the caller's runAction() - swallow so it doesn't
      // also surface as an unhandled promise rejection.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Complete validation"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            loading={saving}
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Complete
          </Button>
        </>
      }
    >
      <Stack spacing={2.5} sx={{ pt: 1 }}>
        <Input
          select
          label="Validation result"
          value={validationResult}
          onChange={(event) => setValidationResult(event.target.value as ValidationResult)}
          required
          autoFocus
        >
          {ALL_VALIDATION_RESULTS.map((result) => (
            <MenuItem key={result} value={result}>
              {result}
            </MenuItem>
          ))}
        </Input>

        <Input
          label="Summary"
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          multiline
          minRows={3}
          required
        />

        <Input
          label="Actual completion date"
          type="date"
          value={actualCompletionDate}
          onChange={(event) => setActualCompletionDate(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          required
        />

        {isLate ? (
          <>
            <Alert severity="warning">
              This is after the expected completion date ({expectedCompletionDate}) - an extension
              reason is required.
            </Alert>
            <Input
              label="Extension reason"
              value={extensionReason}
              onChange={(event) => setExtensionReason(event.target.value)}
              multiline
              minRows={2}
              required
              placeholder="e.g. waiting for new build, hardware unavailable, requirement changed..."
            />
          </>
        ) : null}
      </Stack>
    </Modal>
  );
}
